import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import { storage } from '@/configs/storage';
import { APP_SETTING_STORE_KEY } from '@/features/profile-setting/constants/store';
import { Locale } from '@/localization/types/localizationTypes';
import { updateLocalizationInstance } from '@/localization/utils/localization';
import { AppThemeMode } from '@/styles/type/appThemeMode';

export type AppSettingStore = {
  selectedLocale: Locale;
  updateSelectedLocale: (locale: Locale) => void;
  toggleLocale: () => void;
  selectedTheme: AppThemeMode;
  updateSelectedTheme: (theme: AppThemeMode) => void;
  isDarkModeEnabled: boolean;
  updateIsDarkModeEnabled: (enabled: boolean) => void;
  isExtensionDebugFrameEnabled: boolean;
  updateIsExtensionDebugFrameEnabled: (enabled: boolean) => void;
};

export const useAppSettingStore = create<AppSettingStore>()(
  persist(
    (set, get) => ({
      selectedLocale: Locale.DE,
      updateSelectedLocale: (locale: Locale) => {
        updateLocalizationInstance(locale);
        set({ selectedLocale: locale });
      },
      toggleLocale: () => {
        const currentLocale = get().selectedLocale;
        const desiredLocale =
          currentLocale === Locale.EN ? Locale.DE : Locale.EN;
        updateLocalizationInstance(desiredLocale);
        set({ selectedLocale: desiredLocale });
      },
      selectedTheme: AppThemeMode.AUTO,
      updateSelectedTheme: (theme: AppThemeMode) =>
        set({ selectedTheme: theme }),
      isDarkModeEnabled: false,
      updateIsDarkModeEnabled: (enabled: boolean) =>
        set({ isDarkModeEnabled: enabled }),
      isExtensionDebugFrameEnabled: true,
      updateIsExtensionDebugFrameEnabled: (enabled: boolean) =>
        set({ isExtensionDebugFrameEnabled: enabled })
    }),
    {
      name: APP_SETTING_STORE_KEY,
      storage: createJSONStorage(() => storage)
    }
  )
);
